import React, { useRef, useEffect, useState } from "react";
import { PrimaryButton } from "@/components/Buttons.jsx";
import EmailEditor from "react-email-editor";

const EmailPreviewTile = ({ 
  emailContent, 
  emailDesign, 
  title = "Email Content", 
  description = "This is the content that will be sent to the customer.",
  onEdit,
  readOnly = false,
  buttonText = "Edit Email Content"
}) => {
  const emailEditorRef = useRef();
  const [previewHtml, setPreviewHtml] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);


  // Generate preview HTML from design or content
  useEffect(() => {
    if (emailDesign && emailEditorRef.current && emailEditorRef.current.editor) {
      setIsGenerating(true);
      
      // Load the design first
      emailEditorRef.current.editor.loadDesign(emailDesign);
      
      // Wait for design to load, then export HTML
      setTimeout(() => {
        if (emailEditorRef.current && emailEditorRef.current.editor) {
          emailEditorRef.current.editor.exportHtml((data) => {
            // Replace merge tags with sample values for preview
            let previewHtml = data.html;
            
            // Define sample values for merge tags
            const sampleValues = {
              '{contactfield=firstname}': 'John',
              '{contactfield=lastname}': 'Doe',
              '{contactfield=last_added_item_name}': 'Premium Product 250mL',
              '{contactfield=last_added_item_image}': 'https://cdn.saucey.com/direct/web/crm-placehold-product.png',
              '{contactfield=cart_items_count}': '3',
              '{contactfield=cart_total}': '$149.99',
              '{contactfield=store_name}': 'Your Store',
              '{contactfield=store_url}': 'https://yourstore.com',
              '{contactfield=checkout_url}': 'https://yourstore.com/checkout',
              '{contactfield=cart_url}': 'https://yourstore.com/cart'
            };
            
            // Replace all merge tags with sample values
            Object.entries(sampleValues).forEach(([tag, value]) => {
              previewHtml = previewHtml.replace(new RegExp(tag.replace(/[{}]/g, '\\$&'), 'g'), value);
            });
            
            setPreviewHtml(previewHtml);
            setIsGenerating(false);
          });
        }
      }, 2000);
    } else if (emailContent) {
      // Use existing HTML content directly
      let previewHtml = emailContent;
      
      // Replace merge tags with sample values
      const sampleValues = {
        '{contactfield=firstname}': 'John',
        '{contactfield=lastname}': 'Doe',
        '{contactfield=last_added_item_name}': 'Premium Product 250mL',
        '{contactfield=last_added_item_image}': 'https://cdn.saucey.com/direct/web/crm-placehold-product.png',
        '{contactfield=cart_items_count}': '3',
        '{contactfield=cart_total}': '$149.99',
        '{contactfield=store_name}': 'Your Store',
        '{contactfield=store_url}': 'https://yourstore.com',
        '{contactfield=checkout_url}': 'https://yourstore.com/checkout',
        '{contactfield=cart_url}': 'https://yourstore.com/cart'
      };
      
      Object.entries(sampleValues).forEach(([tag, value]) => {
        previewHtml = previewHtml.replace(new RegExp(tag.replace(/[{}]/g, '\\$&'), 'g'), value);
      });
      
      setPreviewHtml(previewHtml);
    }
  }, [emailContent, emailDesign]);

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  const hasContent = emailContent || emailDesign;

  return (
    <div className="flex items-center gap-4 border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-900/20 mb-6">
      <div className="border border-slate-200 dark:border-slate-600 rounded overflow-hidden bg-white relative">
        <div 
          className="overflow-hidden rounded relative cursor-pointer"
          style={{ 
            width: '200px', 
            height: '200px'
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {isGenerating ? (
            <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-700 dark:to-slate-600 flex flex-col items-center justify-center rounded">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <span className="text-slate-600 dark:text-slate-300 text-sm font-medium">
                Loading Preview
              </span>
            </div>
          ) : previewHtml ? (
            <div
              dangerouslySetInnerHTML={{ __html: previewHtml }}
              style={{
                transform: 'scale(0.5)',
                transformOrigin: 'top left',
                width: '200%',
                height: '400px'
              }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-700 dark:to-slate-600 flex flex-col items-center justify-center rounded">
              <div className="text-4xl mb-2">📧</div>
              <span className="text-slate-600 dark:text-slate-300 text-sm font-medium">
                {title}
              </span>
              <span className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                No content defined
              </span>
            </div>
          )}
          
          {/* Hover overlay */}
          {isHovered && hasContent && !isGenerating && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded transition-opacity duration-200">
              <PrimaryButton onClick={handleEdit} size="sm">
                {readOnly ? 'View Content' : buttonText}
              </PrimaryButton>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex-1">
        <div className="font-semibold text-slate-800 dark:text-slate-100 mb-1">{title}</div>
        <div className="text-slate-500 text-sm mb-1">{description}</div>
        {hasContent && (
          <div className="text-xs text-slate-400 dark:text-slate-500">
            Click preview to {readOnly ? 'view' : 'edit'} content
          </div>
        )}
      </div>
      
      <div>
        <PrimaryButton
          type="button"
          onClick={onEdit}
          disabled={readOnly}
        >
          {readOnly ? 'View Email Content' : buttonText}
        </PrimaryButton>
      </div>
      
      {/* Hidden EmailEditor for generating preview from design */}
      {emailDesign && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '600px', height: '400px' }}>
          <EmailEditor
            ref={emailEditorRef}
            minHeight={400}
            options={{
              mergeTags: {
                first_name: {
                  name: 'First Name',
                  value: '{contactfield=firstname}',
                  sample: 'John',
                },
                last_name: {
                  name: 'Last Name',
                  value: '{contactfield=lastname}',
                  sample: 'Doe',
                },
                last_added_item_name: {
                  name: "Last Added Item Name",
                  value: "{contactfield=last_added_item_name}",
                  sample: "Some Product Name",
                },
                last_added_item_image: {
                  name: "Last Added Item Image",
                  value: "{contactfield=last_added_item_image}",
                  sample: "https://cdn.saucey.com/direct/web/crm-placehold-product.png",
                },
              },
              tools: {
                text: {
                  properties: {
                    textAlign: {
                      value: 'center'
                    }
                  }
                }
              },
              features: {
                devTab: false,
              },
              version: '1.157.0'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default EmailPreviewTile;
